import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";

interface SetNameStepProps {
  onSubmit: (data: {
    name: string;
    email: string;
    password: string;
    passwordRepeat: string;
  }) => void;
  errorMsg: string | null;
  loading: boolean;
}

export default function SetNameStep({
  onSubmit,
  errorMsg,
  loading,
}: SetNameStepProps) {
  const { t } = useTranslation("onboarding");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    passwordRepeat: "",
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="space-y-6">
      {errorMsg && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm font-semibold text-destructive"
        >
          {errorMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="name">{t("setName.nameLabel")}</Label>
          <Input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder={t("setName.namePlaceholder")}
            autoComplete="name"
            className="h-11"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">{t("setName.emailLabel")}</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            placeholder={t("setName.emailPlaceholder")}
            autoComplete="username"
            className="h-11"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">{t("setName.passwordLabel")}</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              placeholder="********"
              autoComplete="new-password"
              className="h-11 pr-11"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="passwordRepeat">{t("setName.passwordRepeatLabel")}</Label>
          <div className="relative">
            <Input
              id="passwordRepeat"
              type={showPasswordRepeat ? "text" : "password"}
              value={formData.passwordRepeat}
              onChange={(e) =>
                handleInputChange("passwordRepeat", e.target.value)
              }
              placeholder="********"
              autoComplete="new-password"
              className="h-11 pr-11"
              required
            />
            <button
              type="button"
              onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {showPasswordRepeat ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="h-11 w-full bg-foreground text-background hover:bg-foreground/90"
        >
          {loading ? t("setName.creating") : t("setName.submit")}
        </Button>
      </form>

      <p className="text-xs leading-relaxed text-muted-foreground">
        {t("setName.terms")}{" "}
        <a
          href="https://graftus.ai/villkor-foretag/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          {t("setName.termsLink")}
        </a>{" "}
        {t("setName.and")}{" "}
        <a
          href="https://graftus.ai/integritetspolicy/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          {t("setName.privacyLink")}
        </a>
      </p>
    </div>
  );
}
